import React from 'react';
import { Box, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { useReducedMotionPreferred } from '../hooks/useReducedMotionPreferred';
import { AURORA_GRADIENTS } from '../theme/tokens';

function AuroraBackground() {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const reduced = useReducedMotionPreferred();

  const base = isLight ? AURORA_GRADIENTS.auroraLightBase : AURORA_GRADIENTS.auroraDarkBase;

  const orbs = [
    {
      key: 'amber',
      gradient: AURORA_GRADIENTS.auroraOrbAmber,
      size: { xs: 340, sm: 460 },
      top: '5%',
      left: '-8%',
      opacity: isLight ? 0.62 : 0.45,
      driftX: [0, 28, -14, 0],
      driftY: [0, -18, 24, 0],
      duration: 14,
    },
    {
      key: 'orange',
      gradient: AURORA_GRADIENTS.auroraOrbOrange,
      size: { xs: 300, sm: 420 },
      top: '45%',
      right: '-6%',
      opacity: isLight ? 0.55 : 0.38,
      driftX: [0, -22, 16, 0],
      driftY: [0, 20, -12, 0],
      duration: 18,
    },
    {
      key: 'navy',
      gradient: isLight ? AURORA_GRADIENTS.auroraOrbNavyLight : AURORA_GRADIENTS.auroraOrbNavyDark,
      size: { xs: 280, sm: 380 },
      bottom: '4%',
      left: '30%',
      opacity: isLight ? 0.70 : 0.55,
      driftX: [0, 18, -28, 0],
      driftY: [0, -24, 10, 0],
      duration: 22,
    },
  ];

  const noiseOpacity = isLight ? 0.04 : 0.06;
  const noiseSvg = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='4' height='4'%3E%3Ccircle cx='1' cy='1' r='0.8' fill='%23000' opacity='1'/%3E%3C/svg%3E")`;

  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        background: base,
        overflow: 'hidden',
        pointerEvents: 'none',
        '@media print': { display: 'none' },
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: noiseSvg,
          backgroundRepeat: 'repeat',
          opacity: noiseOpacity,
          pointerEvents: 'none',
        },
      }}
    >
      {orbs.map((orb) => {
        const animateProps = reduced
          ? {
              x: orb.driftX[1] / 2,
              y: orb.driftY[1] / 2,
              scale: 1,
            }
          : {
              x: orb.driftX,
              y: orb.driftY,
              scale: [1, 1.08, 1],
            };

        const transitionProps = reduced
          ? {}
          : {
              duration: orb.duration,
              ease: 'easeInOut',
              repeat: Infinity,
              repeatType: 'mirror',
            };

        return (
          <Box
            key={orb.key}
            sx={{
              position: 'absolute',
              top: orb.top,
              left: orb.left,
              right: orb.right,
              bottom: orb.bottom,
              width: { xs: orb.size.xs, sm: orb.size.sm },
              height: { xs: orb.size.xs, sm: orb.size.sm },
            }}
          >
            <motion.div
              animate={animateProps}
              transition={transitionProps}
              style={{
                width: '100%',
                height: '100%',
                background: orb.gradient,
                opacity: orb.opacity,
                filter: 'blur(60px)',
                borderRadius: '50%',
                willChange: reduced ? 'auto' : 'transform',
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
}

export default AuroraBackground;
